import type { Pool, PoolStatus } from "../shared/types";
import { SAMPLE_POOLS } from "./sample";
import { getAllStatuses, getPoolStatus, listPools } from "./api";

export interface PoolsLoad {
  pools: Pool[];
  /** True when the live API answered; false when we fell back to sample data. */
  live: boolean;
}

let poolsCache: PoolsLoad | null = null;
const statusCache = new Map<string, PoolStatus>();
// Dedup guard for the batch status warm-up so list -> map navigation (and the
// filtered/unfiltered list paths) share a single fetch.
let allStatusesPromise: Promise<void> | null = null;

/**
 * Load the pool catalog once, falling back to the offline sample data.
 *
 * Every real public pool is shown. The app's focus stays on children's swimming
 * via the kiddie/focus section, badges, and favorite/distance ordering — but
 * main-only pools (e.g. Hearst, Langdon Park, Volta Park) are no longer hidden,
 * so a family that just wants the nearest open pool can find one. A main-only
 * pool renders its reportable `main` section (no kiddie section).
 *
 * The only pools filtered out are admin-`deactivated` ones (retired): the live
 * `GET /pools` already excludes them, and this guard keeps the offline sample
 * path consistent. Closed-for-season pools are kept (they surface as a
 * non-reportable "Closed for the season" card), as are splash pads
 * (`kind:"splash"`, for the Map + Splash tabs) and waterslide pools.
 */
const isVisible = (p: Pool): boolean => p.deactivated !== true;

export async function loadPools(): Promise<PoolsLoad> {
  if (poolsCache) return poolsCache;
  try {
    const pools = (await listPools()).filter(isVisible);
    poolsCache = { pools, live: true };
  } catch {
    poolsCache = { pools: SAMPLE_POOLS.filter(isVisible), live: false };
  }
  return poolsCache;
}

/** A previously fetched status, if any (used to render list/map synchronously). */
export function cachedStatus(poolId: string): PoolStatus | undefined {
  return statusCache.get(poolId);
}

/** Record a status (e.g. after an optimistic report) so other views see it. */
export function setStatus(status: PoolStatus): void {
  statusCache.set(status.poolId, status);
}

/** Fetch a status, caching the result. Returns undefined when the API is down. */
export async function fetchStatus(poolId: string): Promise<PoolStatus | undefined> {
  try {
    const status = await getPoolStatus(poolId);
    statusCache.set(poolId, status);
    return status;
  } catch {
    return undefined;
  }
}

/**
 * Warm the status cache for every pool in one batch request (replaces the
 * homepage per-pool fan-out). Deduped via a module-level promise so concurrent
 * callers and repeat navigations reuse one fetch. On failure the promise is
 * reset so a later call can retry; the cache is simply left unwarmed (callers
 * fall back to "…"/unknown, matching the prior offline behavior).
 */
export function loadAllStatuses(): Promise<void> {
  if (!allStatusesPromise) {
    allStatusesPromise = getAllStatuses()
      .then((list) => {
        for (const s of list) statusCache.set(s.poolId, s);
      })
      .catch(() => {
        allStatusesPromise = null; // allow a later retry
      });
  }
  return allStatusesPromise;
}
